<script setup lang="ts">
import type {
  OpenImConversationSummary,
  OpenImMessageItem,
  OpenImOrderChatPayload,
} from '@/api/modules/openim'
import { FaAvatar, FaEmpty } from '@fantastic-admin/components'
import { ElImage } from 'element-plus'
import apiOpenim from '@/api/modules/openim'
import dayjs from '@/utils/dayjs'

interface Props {
  modelValue: boolean
  mode: 'user' | 'order'
  userId?: number
  conversationId?: string
  targetUserId?: number
  orderId?: number
  orderMessagesRequest?: (params: Record<string, unknown>) => Promise<{ data: OpenImOrderChatPayload }>
}

defineOptions({
  name: 'OpenImChatRecordDialog',
})

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const visible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const loading = ref(false)
const messageLoading = ref(false)
const currentPage = ref(1)
const conversations = ref<OpenImConversationSummary[]>([])
const currentConversation = ref<OpenImConversationSummary | null>(null)
const messages = ref<OpenImMessageItem[]>([])
const hasMore = ref(false)
const orderMeta = ref<OpenImOrderChatPayload | null>(null)

const ownerUserId = computed(() => {
  if (props.mode === 'user') {
    return Number(props.userId || 0)
  }

  return Number(orderMeta.value?.buyer_user_id || 0)
})

const dialogTitle = computed(() => props.mode === 'user' ? '用户交流记录' : '订单交流记录')

function resolveConversationName(conversation: OpenImConversationSummary | null) {
  if (!conversation) {
    return '聊天记录'
  }

  return conversation.target_user?.nickname
    || conversation.target_user?.username
    || conversation.latest_message?.sender_name
    || `用户 #${conversation.target_user_id}`
}

const currentConversationTitle = computed(() => {
  if (props.mode === 'order') {
    if (!orderMeta.value) {
      return '订单聊天记录'
    }

    return `订单 #${orderMeta.value.order_no || orderMeta.value.order_id}`
  }

  return resolveConversationName(currentConversation.value)
})

const messagePanelSubtitle = computed(() => {
  if (props.mode === 'order') {
    return `${resolveOrderUserName('buyer')} 与 ${resolveOrderUserName('seller')} 的 OpenIM 对话记录`
  }

  return currentConversation.value
    ? `当前展示与 ${resolveConversationName(currentConversation.value)} 的 OpenIM 单聊历史`
    : '当前展示 OpenIM 单聊历史消息'
})

function formatMessageTime(value: number) {
  if (!value) {
    return '-'
  }

  return dayjs(value).format('YYYY-MM-DD HH:mm:ss')
}

function formatConversationTime(value: number) {
  if (!value) {
    return '暂无时间'
  }

  return dayjs(value).format('MM-DD HH:mm')
}

function isSelfMessage(item: OpenImMessageItem) {
  return Number(item.send_id || 0) === ownerUserId.value
}

function isRightAlignedMessage(item: OpenImMessageItem) {
  if (props.mode === 'order') {
    return Number(item.send_id || 0) === Number(orderMeta.value?.seller_user_id || 0)
  }

  return isSelfMessage(item)
}

function resolveSenderName(item: OpenImMessageItem) {
  if (props.mode === 'order') {
    const sendId = Number(item.send_id || 0)
    if (sendId === Number(orderMeta.value?.buyer_user_id || 0)) {
      return resolveOrderUserName('buyer')
    }
    if (sendId === Number(orderMeta.value?.seller_user_id || 0)) {
      return resolveOrderUserName('seller')
    }
  }

  if (isSelfMessage(item)) {
    return '我方视角'
  }

  return item.sender_nickname || `用户 #${item.send_id || '-'}`
}

function resolveAvatarText(item: OpenImMessageItem) {
  return resolveSenderName(item).slice(0, 2).toUpperCase()
}

function resolveAvatarSrc(item: OpenImMessageItem) {
  if (props.mode === 'order') {
    const sendId = Number(item.send_id || 0)
    if (sendId === Number(orderMeta.value?.buyer_user_id || 0)) {
      return orderMeta.value?.buyer_user?.avatar || item.sender_face_url || ''
    }
    if (sendId === Number(orderMeta.value?.seller_user_id || 0)) {
      return orderMeta.value?.seller_user?.avatar || item.sender_face_url || ''
    }
  }

  return item.sender_face_url || ''
}

function resolveOrderUserName(role: 'buyer' | 'seller') {
  const user = role === 'buyer' ? orderMeta.value?.buyer_user : orderMeta.value?.seller_user
  const fallbackId = role === 'buyer' ? orderMeta.value?.buyer_user_id : orderMeta.value?.seller_user_id
  const roleLabel = role === 'buyer' ? '买家' : '卖家'

  return user?.nickname || user?.username || `${roleLabel} #${fallbackId || '-'}`
}

function resolveConversationAvatar(conversation: OpenImConversationSummary) {
  return conversation.target_user?.avatar || conversation.latest_message?.sender_face_url || ''
}

function resolveConversationAvatarFallback(conversation: OpenImConversationSummary) {
  const label = resolveConversationName(conversation)

  return label.slice(0, 2).toUpperCase()
}

function isImageMessage(item: OpenImMessageItem) {
  return Boolean(item.media_url && item.content_type === 102)
}

function showMessageText(item: OpenImMessageItem) {
  return !(isImageMessage(item) && item.display_text === '[图片]')
}

function resolveMessageBadge(item: OpenImMessageItem) {
  if (item.content_type === 102) {
    return '图片'
  }
  if (item.content_type === 103) {
    return '语音'
  }
  if (item.content_type === 104) {
    return '视频'
  }
  if (item.content_type === 105) {
    return '文件'
  }

  return ''
}

async function loadUserConversationList() {
  const userId = Number(props.userId || 0)
  if (userId <= 0) {
    conversations.value = []
    currentConversation.value = null
    messages.value = []
    return
  }

  const res = await apiOpenim.userConversations(userId)
  conversations.value = res.data.list ?? []
  currentConversation.value = conversations.value.find((item) => {
    if (props.conversationId && item.conversation_id === props.conversationId) {
      return true
    }
    return Number(props.targetUserId || 0) > 0 && Number(item.target_user_id) === Number(props.targetUserId)
  }) ?? conversations.value[0] ?? null

  if (!currentConversation.value) {
    messages.value = []
    hasMore.value = false
    return
  }

  await loadUserMessages(1)
}

async function loadUserMessages(page = 1) {
  const userId = Number(props.userId || 0)
  const targetUserId = Number(currentConversation.value?.target_user_id || 0)
  if (userId <= 0 || targetUserId <= 0) {
    messages.value = []
    hasMore.value = false
    return
  }

  messageLoading.value = true
  try {
    const res = await apiOpenim.userMessages(userId, {
      target_user_id: targetUserId,
      conversation_id: currentConversation.value?.conversation_id || '',
      page,
      per_page: 20,
    })
    currentPage.value = res.data.page ?? page
    messages.value = res.data.list ?? []
    hasMore.value = Boolean(res.data.has_more)
  }
  finally {
    messageLoading.value = false
  }
}

async function loadOrderMessages(page = 1) {
  const orderId = Number(props.orderId || 0)
  if (orderId <= 0) {
    orderMeta.value = null
    messages.value = []
    hasMore.value = false
    return
  }

  messageLoading.value = true
  try {
    const params = {
      page,
      per_page: 20,
    }
    const res = props.orderMessagesRequest
      ? await props.orderMessagesRequest(params)
      : await apiOpenim.orderMessages(orderId, params)
    orderMeta.value = res.data
    currentPage.value = res.data.messages.page ?? page
    messages.value = res.data.messages.list ?? []
    hasMore.value = Boolean(res.data.messages.has_more)
  }
  finally {
    messageLoading.value = false
  }
}

async function loadDialogData() {
  if (!visible.value) {
    return
  }

  loading.value = true
  conversations.value = []
  currentConversation.value = null
  orderMeta.value = null
  messages.value = []
  hasMore.value = false
  currentPage.value = 1

  try {
    if (props.mode === 'user') {
      await loadUserConversationList()
    }
    else {
      await loadOrderMessages(1)
    }
  }
  finally {
    loading.value = false
  }
}

async function handleConversationClick(item: OpenImConversationSummary) {
  if (messageLoading.value || currentConversation.value?.conversation_id === item.conversation_id) {
    return
  }

  currentConversation.value = item
  await loadUserMessages(1)
}

async function handlePrevPage() {
  const nextPage = currentPage.value + 1
  if (props.mode === 'user') {
    await loadUserMessages(nextPage)
  }
  else {
    await loadOrderMessages(nextPage)
  }
}

async function handleNextPage() {
  const nextPage = 1
  if (props.mode === 'user') {
    await loadUserMessages(nextPage)
  }
  else {
    await loadOrderMessages(nextPage)
  }
}

watch(
  () => [visible.value, props.mode, props.userId, props.conversationId, props.targetUserId, props.orderId],
  async () => {
    if (visible.value) {
      await loadDialogData()
    }
  },
)
</script>

<template>
  <FaModal
    v-model="visible"
    :title="dialogTitle"
    destroy-on-close
    class="sm:max-w-[1240px]"
    :footer="false"
  >
    <div v-loading="loading" class="openim-chat-dialog">
      <div v-if="mode === 'user'" class="openim-chat-layout">
        <div class="conversation-list">
          <div class="panel-title conversation-list-title">
            最近交流对象
          </div>

          <div v-if="conversations.length" class="conversation-items">
            <button
              v-for="item in conversations"
              :key="item.conversation_id"
              type="button"
              class="conversation-item"
              :class="{ 'is-active': currentConversation?.conversation_id === item.conversation_id }"
              @click="handleConversationClick(item)"
            >
              <FaAvatar
                :src="resolveConversationAvatar(item)"
                :fallback="resolveConversationAvatarFallback(item)"
                shape="square"
                class="conversation-avatar"
              />
              <div class="conversation-content">
                <div class="conversation-topline">
                  <div class="conversation-name">
                    {{ resolveConversationName(item) }}
                  </div>
                  <div class="conversation-time">
                    {{ formatConversationTime(item.latest_message?.send_time_ms || 0) }}
                  </div>
                </div>
                <div class="conversation-preview">
                  {{ item.latest_message?.display_text || '暂无消息' }}
                </div>
              </div>
            </button>
          </div>

          <FaEmpty v-else title="该用户暂无单聊记录" />
        </div>

        <div class="message-panel">
          <div class="panel-header">
            <div>
              <div class="panel-title">
                {{ currentConversationTitle }}
              </div>
              <div class="panel-subtitle">
                {{ messagePanelSubtitle }}
              </div>
            </div>
            <div class="panel-actions">
              <FaButton variant="outline" size="sm" :disabled="messageLoading" @click="handleNextPage">
                刷新最新
              </FaButton>
              <FaButton variant="outline" size="sm" :disabled="!hasMore || messageLoading" @click="handlePrevPage">
                更早记录
              </FaButton>
            </div>
          </div>

          <div v-loading="messageLoading" class="message-list">
            <FaEmpty v-if="!messages.length" title="暂无聊天记录" />

            <div
              v-for="item in messages"
              :key="`${item.seq}-${item.send_time_ms}`"
              class="message-row"
              :class="{ 'is-right': isRightAlignedMessage(item) }"
            >
              <FaAvatar
                :src="resolveAvatarSrc(item)"
                :fallback="resolveAvatarText(item)"
                shape="square"
                class="message-avatar"
              />
              <div class="message-stack">
                <div class="message-meta">
                  <span class="message-sender">{{ resolveSenderName(item) }}</span>
                  <span>{{ formatMessageTime(item.send_time_ms) }}</span>
                </div>
                <div class="message-bubble" :class="{ 'is-image-only': isImageMessage(item) && !showMessageText(item) }">
                  <div v-if="resolveMessageBadge(item)" class="message-badge">
                    {{ resolveMessageBadge(item) }}
                  </div>
                  <div v-if="showMessageText(item)" class="message-text">
                    {{ item.display_text || item.content_raw || '-' }}
                  </div>
                  <ElImage
                    v-if="item.media_url && item.content_type === 102"
                    :src="item.media_url"
                    :preview-src-list="[item.media_url]"
                    fit="cover"
                    class="message-image"
                    preview-teleported
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="message-panel is-order-panel">
        <div class="panel-header">
          <div>
            <div class="panel-title">
              {{ currentConversationTitle }}
            </div>
            <div class="panel-subtitle">
              {{ messagePanelSubtitle }}
            </div>
          </div>
          <div class="panel-actions">
            <FaButton variant="outline" size="sm" :disabled="messageLoading" @click="handleNextPage">
              刷新最新
            </FaButton>
            <FaButton variant="outline" size="sm" :disabled="!hasMore || messageLoading" @click="handlePrevPage">
              更早记录
            </FaButton>
          </div>
        </div>

        <div v-loading="messageLoading" class="message-list">
          <FaEmpty v-if="!messages.length" title="该订单暂未产生聊天记录" />

          <div
            v-for="item in messages"
            :key="`${item.seq}-${item.send_time_ms}`"
            class="message-row"
            :class="{ 'is-right': isRightAlignedMessage(item) }"
          >
            <FaAvatar
              :src="resolveAvatarSrc(item)"
              :fallback="resolveAvatarText(item)"
              shape="square"
              class="message-avatar"
            />
            <div class="message-stack">
              <div class="message-meta">
                <span class="message-sender">{{ resolveSenderName(item) }}</span>
                <span>{{ formatMessageTime(item.send_time_ms) }}</span>
              </div>
              <div class="message-bubble" :class="{ 'is-image-only': isImageMessage(item) && !showMessageText(item) }">
                <div v-if="resolveMessageBadge(item)" class="message-badge">
                  {{ resolveMessageBadge(item) }}
                </div>
                <div v-if="showMessageText(item)" class="message-text">
                  {{ item.display_text || item.content_raw || '-' }}
                </div>
                <ElImage
                  v-if="item.media_url && item.content_type === 102"
                  :src="item.media_url"
                  :preview-src-list="[item.media_url]"
                  fit="cover"
                  class="message-image"
                  preview-teleported
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </FaModal>
</template>

<style scoped>
.openim-chat-dialog {
  min-height: 560px;

  --openim-surface: linear-gradient(180deg, rgb(248 250 252 / 96%), rgb(255 255 255 / 98%));
  --openim-border: rgb(148 163 184 / 16%);
  --openim-shadow: 0 20px 40px rgb(15 23 42 / 8%);
  --openim-bubble-other: #fff;
  --openim-bubble-self: linear-gradient(135deg, #dff3ff 0%, #eef7ff 100%);
  --openim-accent: #409eff;
}

.openim-chat-layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 16px;
  min-height: 560px;
}

.conversation-list,
.message-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--openim-surface);
  border: 1px solid var(--openim-border);
  border-radius: 20px;
  box-shadow: var(--openim-shadow);
  backdrop-filter: blur(12px);
}

.conversation-list {
  overflow: hidden;
}

.panel-header,
.panel-title {
  color: var(--el-text-color-primary);
}

.conversation-list-title {
  padding: 18px 20px 12px;
}

.panel-header {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.panel-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
}

.panel-subtitle {
  margin-top: 4px;
  font-size: 12px;
  line-height: 18px;
  color: var(--el-text-color-secondary);
}

.panel-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.conversation-items,
.message-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.conversation-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  padding: 16px 18px;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--el-border-color-lighter);
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.conversation-item:hover,
.conversation-item.is-active {
  background: linear-gradient(135deg, rgb(64 158 255 / 12%), rgb(64 158 255 / 4%));
}

.conversation-item:hover {
  transform: translateY(-1px);
}

.conversation-avatar {
  flex: none;
  width: 42px;
  height: 42px;
}

.conversation-content {
  flex: 1;
  min-width: 0;
}

.conversation-topline {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
}

.conversation-name {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--el-text-color-primary);
  white-space: nowrap;
}

.conversation-preview,
.conversation-time {
  margin-top: 6px;
  font-size: 12px;
  line-height: 18px;
  color: var(--el-text-color-secondary);
}

.conversation-preview {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 1;
  word-break: normal;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
}

.conversation-time {
  flex: none;
  margin-top: 0;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 22px 20px 24px;
  background:
    radial-gradient(circle at top left, rgb(64 158 255 / 5%), transparent 24%),
    linear-gradient(180deg, rgb(255 255 255 / 70%), rgb(248 250 252 / 92%));
}

.message-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.message-row.is-right {
  flex-direction: row-reverse;
}

.message-avatar {
  flex: none;
  width: 36px;
  height: 36px;
  margin-top: 22px;
}

.message-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  max-width: min(720px, calc(100% - 48px));
}

.message-row.is-right .message-stack {
  align-items: flex-end;
}

.message-meta {
  display: flex;
  gap: 10px;
  font-size: 12px;
  line-height: 18px;
  color: var(--el-text-color-secondary);
}

.message-sender {
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.message-bubble {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: min(640px, 100%);
  padding: 12px 14px;
  background: var(--openim-bubble-other);
  border: 1px solid rgb(226 232 240 / 90%);
  border-radius: 18px 18px 18px 6px;
  box-shadow: 0 10px 24px rgb(15 23 42 / 7%);
}

.message-row.is-right .message-bubble {
  background: var(--openim-bubble-self);
  border-color: rgb(64 158 255 / 24%);
  border-radius: 18px 18px 6px;
}

.message-bubble.is-image-only {
  width: fit-content;
  max-width: min(280px, 100%);
  padding: 10px;
}

.message-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 600;
  color: var(--openim-accent);
  background: rgb(64 158 255 / 10%);
  border-radius: 999px;
}

.message-text {
  font-size: 14px;
  line-height: 22px;
  color: var(--el-text-color-primary);
  word-break: normal;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.message-image {
  display: block;
  width: auto;
  min-width: 180px;
  max-width: min(260px, 100%);
  max-height: 360px;
  margin-top: 4px;
  overflow: hidden;
  border-radius: 14px;
}

.is-order-panel {
  min-height: 560px;
}

@media (width <= 960px) {
  .openim-chat-layout {
    grid-template-columns: 1fr;
  }

  .conversation-list {
    max-height: 240px;
  }

  .message-bubble {
    max-width: 100%;
  }

  .message-stack {
    max-width: calc(100% - 46px);
  }
}
</style>
